import { WebViewerInstance } from '@pdftron/webviewer';
import { useEffect } from 'react';

import { SUGGESTION_SUBJECT } from 'modules/common/viewer/pdfViewer/annotationCommands';
import { useCanDownload } from 'modules/common/viewer/pdfViewer/hooks/useCanDownload';

type UseCustomDownloadOptionsProps = {
  instance: WebViewerInstance | null;
  filename: string | null;
  isLoaded: boolean;
};

export const useCustomDownloadOptions = ({
  instance,
  filename,
  isLoaded,
}: UseCustomDownloadOptionsProps) => {
  const canDownload = useCanDownload();

  // handle configuring the custom download options
  useEffect(() => {
    if (!instance) return;
    if (!isLoaded) return;

    const { UI, Core } = instance;
    const { annotationManager } = Core;

    const DATA_ELEMENTS = {
      DOWNLOAD_NO_ANNOTATIONS: 'customDownloadNoAnnotations',
      DOWNLOAD_WITH_ANNOTATIONS: 'customDownloadWithAnnotations',
    };
    const allCustomElements = new Set([
      DATA_ELEMENTS.DOWNLOAD_WITH_ANNOTATIONS,
      DATA_ELEMENTS.DOWNLOAD_NO_ANNOTATIONS,
    ]);
    const mainMenuFlyout = UI.Flyouts.getFlyout('MainMenuFlyout');

    /*
    filtering out any download buttons before adding in our custom ones protect against
     duplicate download buttons being created every time the WebViewer re-renders e.g. when switching between files
    */
    const existingManuItems = mainMenuFlyout.items.filter(
      (item) => !allCustomElements.has(item.dataElement),
    );

    if (canDownload) {
      mainMenuFlyout.setItems([
        {
          className: 'row',
          icon: 'icon-download',
          onClick: async () => {
            const saveOptions = Core.SaveOptions;
            const options = {
              filename,
              includeAnnotations: false,
              flags: saveOptions.LINEARIZED,
              downloadType: 'pdf',
            };
            UI.downloadPdf(options);
          },
          dataElement: DATA_ELEMENTS.DOWNLOAD_NO_ANNOTATIONS,
          label: 'Download',
        },
        {
          className: 'row',
          icon: 'icon-download',
          onClick: async () => {
            const xfdfString = await annotationManager.exportAnnotations({
              annotationList: annotationManager
                .getAnnotationsList()
                .filter((annot) => annot.Subject !== SUGGESTION_SUBJECT),
              widgets: false,
              links: false,
              fields: false,
              useDisplayAuthor: false,
            });
            const saveOptions = Core.SaveOptions;
            const options = {
              filename,
              xfdfString,
              flags: saveOptions.LINEARIZED,
              downloadType: 'pdf',
            };
            UI.downloadPdf(options);
          },
          dataElement: DATA_ELEMENTS.DOWNLOAD_WITH_ANNOTATIONS,
          label: 'Download With Annotations',
        },
        ...existingManuItems,
      ]);
    }
  }, [canDownload, filename, instance, isLoaded]);
};
